import { get, isEmpty } from 'lodash';
import { createSelector } from 'reselect';

import { Recommendation } from 'app/types/entities';
import {
  makeGetFeatureFlag,
  makeHasSelectedRecommendation,
  makeSelectAnnualSettings,
  makeSelectCommercialField,
  makeSelectConfigField,
  makeSelectGlobalField,
  makeSelectJvpField,
} from 'Containers/App/selectors';
import {
  makeGetCoveredHouseholdMemberCount,
  makeGetSelectedPublicationPlanYear,
  makeGetTierCodeData,
  makeSelectPolicyholder,
  makeSelectValidDependents,
  makeSelectValidPolicyholderOrUndefined,
  makeSelectValidSpouseOrUndefined,
} from 'Containers/ProfilePage/selectors';
import { Member, MEMBER_TYPE } from 'Containers/ProfilePage/types';
import Plan from 'Models/plan';
import { ElectionInput, ElectionMemberInput } from 'Utils/apiTypes';
import { getAnnualSettingsForYear } from 'Utils/federalLimits';
import { isCustomPlan } from 'Utils/helpers';
import { calculateHsaRecommendation } from 'Utils/hsa';

// TODO: Rewrite this as a real selector once we store plan_selection_id in redux
const makeSelectPlanSelectionId = () => localStorage.getItem('plan_selection_id');

const makeGetSortedRecommendations = () =>
  createSelector(makeSelectCommercialField('recommendations'), (recommendations) =>
    recommendations.sort((a, b) => {
      if (a.score > b.score) return -1;
      if (a.score < b.score) return 1;
      if (a.costs.real_cost < b.costs.real_cost) return -1;
      if (a.costs.real_cost > b.costs.real_cost) return 1;
      return 0;
    }),
  );

// Shared base for every HSA display, so the individual limit, the family limit, and the
// plan year they belong to always move together instead of being resolved separately.
const makeGetHsaContributionLimits = () =>
  createSelector(
    [
      makeSelectGlobalField('hsaContributionLimitIndividual'),
      makeSelectGlobalField('hsaContributionLimitFamily'),
      makeGetFeatureFlag('is_federal_limits_from_jvp_enabled'),
      makeSelectAnnualSettings(),
      makeGetSelectedPublicationPlanYear(),
    ],
    (
      indvLimit,
      familyLimit,
      isJvpLimitsEnabled,
      annualSettings,
      selectedPlanYear,
    ): { individual: number; family: number } => {
      const jvpHsa = isJvpLimitsEnabled
        ? getAnnualSettingsForYear(annualSettings, selectedPlanYear)?.constants?.hsa
        : undefined;
      return {
        individual: jvpHsa?.individual ?? indvLimit,
        family: jvpHsa?.family ?? familyLimit,
      };
    },
  );

const makeGetIndividualHsaContributionLimit = () =>
  createSelector([makeGetHsaContributionLimits()], ({ individual }): number => individual);

const makeGetFamilyHsaContributionLimit = () =>
  createSelector([makeGetHsaContributionLimits()], ({ family }): number => family);

const makeGetHsaContributionLimit = () =>
  createSelector(
    [makeGetCoveredHouseholdMemberCount(), makeGetHsaContributionLimits()],
    (memberCount, { individual, family }): number => (memberCount > 1 ? family : individual),
  );

const makeGetHdhpMinDeductible = () =>
  createSelector(
    [
      makeGetCoveredHouseholdMemberCount(),
      makeSelectAnnualSettings(),
      makeGetFeatureFlag('is_federal_limits_from_jvp_enabled'),
      makeGetSelectedPublicationPlanYear(),
    ],
    (memberCount, annualSettings, isJvpLimitsEnabled, selectedPlanYear): number | null => {
      // Flag off must show the current calendar year's HDHP minimum, never the selected
      // plan year's — passing undefined here, not selectedPlanYear, is what keeps that true.
      const planYear = isJvpLimitsEnabled ? selectedPlanYear : undefined;
      // The min-deductible copy carries no year label, so if the selected plan year isn't in
      // the list — e.g. a lagging active year in January, when the fetch window is only
      // [current, current+1] — fall back to the current calendar year's entry rather than
      // rendering $0 from a null.
      const settingsForYear =
        getAnnualSettingsForYear(annualSettings, planYear) ?? getAnnualSettingsForYear(annualSettings);
      const hdhp = settingsForYear?.constants?.hdhp;
      if (!hdhp) return null;
      return memberCount > 1 ? hdhp.min_deductible_family : hdhp.min_deductible_individual;
    },
  );

const makeGetPlans = () =>
  createSelector(
    [makeGetSortedRecommendations(), makeSelectConfigField('show_carrier_friendly_score')],
    (recommendations) => recommendations,
  );

const makeGetRecommendationByPlanId = (id: string) =>
  createSelector(
    makeSelectCommercialField('recommendations'),
    (recommendations) => recommendations.find((rec) => rec.plan.external_id === id) as Recommendation,
  );

const makeGetDisplayPdfLink = () =>
  createSelector(makeSelectCommercialField('recommendations'), (recommendations) =>
    recommendations.some((i) => i.plan.benefits_summary_url),
  );

const makeGetCatchupEligible = () =>
  createSelector(
    [makeSelectPolicyholder()],
    (policyholder) => policyholder.isValid && parseInt(policyholder.age, 10) >= 55,
  );

const makeGetRecommendedHsaContribution = () =>
  createSelector(
    [makeSelectCommercialField('selectedHealthPlan'), makeGetHsaContributionLimit()],
    (selectedPlan, federalHsaContributionLimit) => {
      let recommendedHsaContribution = 0;

      if (!isEmpty(selectedPlan)) {
        const medicalCost = Math.floor(selectedPlan.costs.services.in_network);
        const drugCost = Math.floor(selectedPlan.costs.drugs);
        const oopEstimate = medicalCost + drugCost;

        if (
          // eslint-disable-next-line no-prototype-builtins
          selectedPlan.plan.hasOwnProperty('spending_account_contributions')
        ) {
          // calculate recommended contribution using employer hsa contribution from recommendation response
          const employerContribution =
            selectedPlan.plan.spending_account_contributions.hsa_annual.employer_contribution_amount;
          const hsaPercentageMatch =
            selectedPlan.plan.spending_account_contributions.hsa_annual.employer_contribution_percent;
          const employerHsaContributionLimit =
            selectedPlan.plan.spending_account_contributions.hsa_annual.employer_contribution_limit;

          recommendedHsaContribution = calculateHsaRecommendation(
            oopEstimate,
            hsaPercentageMatch || 0,
            employerHsaContributionLimit || 0,
            federalHsaContributionLimit,
            employerContribution || 0,
          );
        }
      }

      return Math.round(recommendedHsaContribution);
    },
  );

const makeGetShowHsaNav = () =>
  createSelector(
    [makeSelectConfigField('hsa_plus_enabled'), makeSelectCommercialField('selectedHealthPlan')],
    (hsaPlusEnabled, selectedPlan) => hsaPlusEnabled && get(selectedPlan, 'plan.hsa_eligible', false),
  );

const makeGetHealthPlanElection = () =>
  createSelector(
    [
      makeSelectJvpField('userId'),
      makeSelectValidPolicyholderOrUndefined(),
      makeSelectValidSpouseOrUndefined(),
      makeSelectValidDependents(),
      makeSelectCommercialField('selectedHealthPlan'),
      makeSelectCommercialField('isCoverageWaived'),
      makeGetTierCodeData(),
      makeGetFeatureFlag('is_election_members_enabled'),
    ],
    (
      userId: string | undefined,
      policyholder: Member | undefined,
      spouse: Member | undefined,
      dependents: Member[],
      selectedHealthPlan,
      isCoverageWaived,
      tierCodeData,
      electionMembersEnabled: boolean | null,
    ): ElectionInput[] => {
      const [tierCode, tierCodeDescription] = tierCodeData;

      const householdMembers = [policyholder, spouse, ...dependents];
      const electionMembers: ElectionMemberInput[] = [];
      householdMembers.forEach((m) => {
        if (m) {
          electionMembers.push({
            member_id: m.member_type === MEMBER_TYPE.POLICYHOLDER && userId ? userId : m?.external_id,
            member_type: m.member_type === MEMBER_TYPE.POLICYHOLDER ? 'User' : 'Dependent',
            is_covered: m?.isCovered,
          });
        }
      });

      // Return waived when isCoverageWaived is true OR when a custom plan is selected
      // Custom plans are ephemeral and cannot be sent to JVP, so we treat them as waived
      if (isCoverageWaived || isCustomPlan(selectedHealthPlan)) {
        const election: ElectionInput = {
          planable_type: 'MedicalPlan',
          waived: true,
        };
        if (electionMembersEnabled) {
          election.election_members = [];
        }
        return [election];
      }

      // should be empty when a user deselects a health plan
      if (isEmpty(selectedHealthPlan)) {
        return [];
      }

      const election: ElectionInput = {
        planable_type: 'MedicalPlan',
        jv_plan_id: selectedHealthPlan?.plan?.external_plan_id,
        tier_code: tierCode,
        tier_code_description: tierCodeDescription,
      };

      if (electionMembersEnabled) {
        election.election_members = electionMembers;
      }

      return [election];
    },
  );

const makeGetHsaCatchUpAmount = () =>
  createSelector(
    [
      makeSelectConfigField('catchup_contribution'),
      makeGetFeatureFlag('is_federal_limits_from_jvp_enabled'),
      makeSelectAnnualSettings(),
      makeGetSelectedPublicationPlanYear(),
    ],
    (catchupContribution, isJvpLimitsEnabled, annualSettings, selectedPlanYear): number => {
      const jvpCatchUp = isJvpLimitsEnabled
        ? getAnnualSettingsForYear(annualSettings, selectedPlanYear)?.constants?.hsa?.catch_up
        : undefined;
      return jvpCatchUp ?? catchupContribution;
    },
  );

const makeSelectUserContributionLimit = () =>
  createSelector(
    [
      makeSelectCommercialField('selectedHealthPlan'),
      makeGetCatchupEligible(),
      makeGetHsaCatchUpAmount(),
      makeGetHsaContributionLimit(),
    ],
    (selectedPlan, isCatchupEligible, catchupContribution, hsaContributionLimit): number => {
      if (isEmpty(selectedPlan)) {
        return 0;
      }
      const plan = new Plan(selectedPlan as Recommendation);
      const employerHsaContribution = plan.isEmployerMatching
        ? plan.employerHsaContributionLimit
        : plan.employerHsaFixedContribution;

      const hsaContributionLimitAdjusted = isCatchupEligible
        ? hsaContributionLimit + catchupContribution
        : hsaContributionLimit;
      const userContributionLimit = hsaContributionLimitAdjusted - (employerHsaContribution || 0);
      return userContributionLimit;
    },
  );

// Whether to show limited-provider (Garner-style) HRA educational content. True when the
// selected plan — or any available plan if none is selected — has a limited-provider
// reimbursement HRA.
const makeGetShowLimitedProviderHraEducation = () =>
  createSelector(
    [makeHasSelectedRecommendation(), makeSelectCommercialField('selectedHealthPlan'), makeGetPlans()],
    (hasSelectedRecommendation, selectedPlan, availablePlans): boolean =>
      hasSelectedRecommendation
        ? new Plan(selectedPlan as Recommendation).hraHasLimitedProviderReimbursement === true
        : availablePlans.some((rec) => new Plan(rec).hraHasLimitedProviderReimbursement === true),
  );

// Whether to show HSA/HRA combo (post-deductible) educational content. True when the
// selected plan — or any available plan if none is selected — is an HSA/HRA combo.
const makeGetShowHsaHraComboEducation = () =>
  createSelector(
    [makeHasSelectedRecommendation(), makeSelectCommercialField('selectedHealthPlan'), makeGetPlans()],
    (hasSelectedRecommendation, selectedPlan, availablePlans): boolean =>
      hasSelectedRecommendation
        ? new Plan(selectedPlan as Recommendation).isHsaHraCombo === true
        : availablePlans.some((rec) => new Plan(rec).isHsaHraCombo === true),
  );

export {
  makeGetSortedRecommendations,
  makeGetShowLimitedProviderHraEducation,
  makeGetShowHsaHraComboEducation,
  makeGetPlans,
  makeGetRecommendationByPlanId,
  makeGetDisplayPdfLink,
  makeGetCatchupEligible,
  makeGetHsaContributionLimit,
  makeGetHsaContributionLimits,
  makeGetIndividualHsaContributionLimit,
  makeGetFamilyHsaContributionLimit,
  makeGetHdhpMinDeductible,
  makeGetRecommendedHsaContribution,
  makeGetShowHsaNav,
  makeSelectPlanSelectionId,
  makeGetHealthPlanElection,
  makeSelectUserContributionLimit,
  makeGetHsaCatchUpAmount,
};
